import { useEffect, useRef, useState } from "react";
import { DiscountModalWrapper } from "../../../Styles/Modals/FinanceModalsStyle";
import { toast } from "react-toastify";
import { getApiEndpoints } from "../../../Services/Api/ApiConfig";
import axiosInstance from "../../../Services/Middleware/AxiosInstance";
import SkeletonLoader from "../../Loader/SkeletonLoader";
import ButtonLoader from "../../Loader/ButtonLoader";

const DiscountModal = ({ isOpenDiscountModal, setIsOpenDiscountModal, selectedDiscount, setSelectedDiscount, refreshDiscounts, fixedName = '', onSaveDraft }) => {
    const api = getApiEndpoints();

    const [isFeeTypesLoading, setIsFeeTypesLoading] = useState(false);
    const [feeTypes, setFeeTypes] = useState([]);
    const [showFeeTypeDropdown, setShowFeeTypeDropdown] = useState(false);
    const [selectedFeeType, setSelectedFeeType] = useState('');
    const [discountName, setDiscountName] = useState('');
    const [discountUnit, setDiscountUnit] = useState('');
    const [discountType, setDiscountType] = useState('');
    const [discountLimit, setDiscountLimit] = useState('');
    const [openDiscountDropdown, setOpenDiscountDropdown] = useState(null);
    const [discountAmount, setDiscountAmount] = useState('');
    const initialDiscountStateRef = useRef(null);
    const recordId = selectedDiscount?.id;
    const isEditMode = Boolean(recordId);
    const showDiscountType = ['Rupees', 'Percentage'].includes(discountUnit);
    const isDiscountTypeDisabled = discountUnit === 'Rupees';
    const showDiscountLimit = discountUnit === 'Percentage' && discountType === 'Approx';
    const isFormValid = discountName.trim() !== '' && discountAmount.trim() !== '' && selectedFeeType.trim() !== '' &&
        showDiscountType && (isDiscountTypeDisabled ? discountType === 'Flat' : ['Approx', 'Flat'].includes(discountType)) &&
        (!showDiscountLimit || discountLimit.trim() !== '');
    const isFormChanged = isEditMode && initialDiscountStateRef.current ? (
        discountName !== initialDiscountStateRef.current.discountName ||
        discountUnit !== initialDiscountStateRef.current.discountUnit ||
        (showDiscountType && discountType !== initialDiscountStateRef.current.discountType) ||
        (showDiscountLimit && discountLimit !== initialDiscountStateRef.current.discountLimit) ||
        discountAmount !== initialDiscountStateRef.current.discountAmount ||
        selectedFeeType !== initialDiscountStateRef.current.selectedFeeType
    ) : false;

    useEffect(() => {
        if (isOpenDiscountModal && selectedDiscount && (recordId || fixedName)) {
            const nextDiscountState = {
                discountName: fixedName || String(selectedDiscount.name || ''),
                discountUnit: selectedDiscount.unit ?? selectedDiscount.discount_unit ?? selectedDiscount.discountUnit ?? '',
                discountType: selectedDiscount.type ?? selectedDiscount.discount_type ?? selectedDiscount.discountType ?? '',
                discountLimit: String(selectedDiscount.discount_limit ?? selectedDiscount.discountLimit ?? ''),
                discountAmount: String(selectedDiscount.amount || ''),
                selectedFeeType: selectedDiscount.fee_type || selectedDiscount.feeType || ''
            };
            if (nextDiscountState.discountUnit === 'Rupees') {
                nextDiscountState.discountType = 'Flat';
            }
            setDiscountName(nextDiscountState.discountName);
            setDiscountUnit(nextDiscountState.discountUnit);
            setDiscountType(nextDiscountState.discountType);
            setDiscountLimit(nextDiscountState.discountLimit);
            setDiscountAmount(nextDiscountState.discountAmount);
            setSelectedFeeType(nextDiscountState.selectedFeeType);
            initialDiscountStateRef.current = nextDiscountState;
        } else {
            setDiscountName(isOpenDiscountModal ? fixedName : '');
            setDiscountUnit('');
            setDiscountType('');
            setDiscountLimit('');
            setDiscountAmount('');
            setSelectedFeeType('');
            initialDiscountStateRef.current = null;
        }
        setShowFeeTypeDropdown(false);
        setOpenDiscountDropdown(null);
    }, [isOpenDiscountModal, selectedDiscount, recordId, fixedName]);

    function closeModal() {
        setSelectedDiscount(null);
        setIsOpenDiscountModal(false);
    }

    const fetchFeeTypes = async () => {
        setIsFeeTypesLoading(true);
        try {
            const response = await axiosInstance.get(api.fetchFeeTypes);
            if (response.data.status === 200) {
                setFeeTypes(response.data.types);
            }
        } catch (error) {
            toast.error(error.response?.data.message || error.message);
        } finally {
            setIsFeeTypesLoading(false);
        }
    }

    useEffect(() => {
        if (showFeeTypeDropdown) {
            fetchFeeTypes();
        }
    }, [showFeeTypeDropdown]);

    const handleSelectFeeType = (type) => {
        setSelectedFeeType(type);
        setShowFeeTypeDropdown(false);
    }

    const handleFeeTypeDropdown = () => {
        setOpenDiscountDropdown(null);
        setShowFeeTypeDropdown(!showFeeTypeDropdown);
    }

    const handleSelectDiscountUnit = (unit) => {
        if (unit === discountUnit) return;
        setDiscountUnit(unit);
        setDiscountType(unit === 'Rupees' ? 'Flat' : '');
        setDiscountLimit('');
        setOpenDiscountDropdown(null);
    };

    const handleSaveDiscount = async (e) => {
        e.preventDefault();
        if (!isFormValid || isFeeTypesLoading || (isEditMode && !isFormChanged)) return;
        setIsFeeTypesLoading(true);
        const payload = {
            name: discountName,
            discountUnit,
            discountType: showDiscountType ? discountType : null,
            discountLimit: showDiscountLimit ? discountLimit : null,
            amount: discountAmount,
            feeType: selectedFeeType,
            ...(recordId ? { id: recordId } : {})
        };
        try {
            if (onSaveDraft) {
                await onSaveDraft({ ...payload, name: fixedName || discountName });
                closeModal();
                return;
            }
            const response = await axiosInstance.post(api.insertFeeDiscount, payload, {
                params: {
                    intent: isEditMode ? 'update' : 'add'
                }
            });
            if (response.data.status === 200) {
                toast.success(response.data.message);
                closeModal();
                refreshDiscounts();
            }
        } catch (error) {
            toast.error(error.response?.data.message || error.message);
        } finally {
            setIsFeeTypesLoading(false);
        }
    }

    return (
        <>
            <DiscountModalWrapper className={isOpenDiscountModal ? 'active' : ''}>
                <div className={`modal_box ${isOpenDiscountModal ? 'active' : ''}`}>
                    <div className="modal_head">
                        <h4>{isEditMode ? 'Edit Discount' : 'Add Discount'}</h4>
                        <div className="close_sec">
                            <a onClick={closeModal}><i className="fa-solid fa-xmark"></i></a>
                        </div>
                    </div>
                    <div className="modal_body">
                        <div className="body_inner">
                            <div className="input_box fullwidth">
                                <span>Discount Name <p>*</p></span>
                                <input type="text" value={discountName} readOnly={Boolean(fixedName)} onChange={(e) => setDiscountName(e.target.value)} />
                            </div>
                            {[
                                { label: 'Discount Unit', value: discountUnit, options: ['Rupees', 'Percentage'], onSelect: handleSelectDiscountUnit, visible: true },
                                { label: 'Discount Type', value: discountType, options: ['Approx', 'Flat'], onSelect: setDiscountType, visible: showDiscountType, disabled: isDiscountTypeDisabled }
                            ].filter(({ visible }) => visible).map(({ label, value, options, onSelect, disabled }) => (
                                <div className="select_box halfwidth" key={label}>
                                    <span>{label} <p>*</p></span>
                                    <div className="dropdown_sec">
                                        <div className="dropdown_btn" aria-disabled={Boolean(disabled)} style={disabled ? { cursor: 'not-allowed', opacity: 0.6 } : undefined} onClick={() => {
                                            if (disabled) return;
                                            setShowFeeTypeDropdown(false);
                                            setOpenDiscountDropdown(openDiscountDropdown === label ? null : label);
                                        }}>
                                            <p>{value}</p>
                                            <i className={`fa-solid fa-angle-down ${openDiscountDropdown === label ? 'active' : ''}`}></i>
                                        </div>
                                        <div className={`dropdown ${!disabled && openDiscountDropdown === label ? 'active' : ''}`}>
                                            <div className="dropdown_inner">
                                                <ul>
                                                    {options.map(option => (
                                                        <li key={option} className={value === option ? 'active' : ''} onClick={() => {
                                                            if (disabled) return;
                                                            onSelect(option);
                                                            setOpenDiscountDropdown(null);
                                                        }}>{option}</li>
                                                    ))}
                                                </ul>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                            <div className="input_box halfwidth">
                                <span>Discount Amount <p>*</p></span>
                                <input type="text" value={discountAmount} onChange={(e) => setDiscountAmount(e.target.value)} />
                            </div>
                            {showDiscountLimit && (
                                <div className="input_box halfwidth">
                                    <span>Discount Limit <p>*</p></span>
                                    <input type="text" value={discountLimit} onChange={(e) => setDiscountLimit(e.target.value)} />
                                </div>
                            )}
                            <div className="select_box halfwidth">
                                <span>Fee Type <p>*</p></span>
                                <div className="dropdown_sec">
                                    <div className="dropdown_btn" onClick={handleFeeTypeDropdown}>
                                        <p>{selectedFeeType}</p>
                                        <i className={`fa-solid fa-angle-down ${showFeeTypeDropdown ? 'active' : ''}`}></i>
                                    </div>
                                    <div className={`dropdown dropUp ${showFeeTypeDropdown ? 'active' : ''}`}>
                                        <div className="dropdown_inner">
                                            <ul>
                                                {
                                                    isFeeTypesLoading ? (
                                                        Array.from({ length: 2 }).map((_, index) => (
                                                            <li key={index}>
                                                                <SkeletonLoader width="100%" height="13px" />
                                                            </li>
                                                        ))
                                                    ) : feeTypes.length > 0 ? (
                                                        feeTypes.map((fee, i) => (
                                                            <li key={i}
                                                                onClick={() => handleSelectFeeType(fee)}
                                                                className={selectedFeeType === fee ? 'active' : ''}
                                                            >
                                                                {fee}
                                                            </li>
                                                        ))
                                                    ) : (
                                                        <li className="empty_message">No fee types available</li>
                                                    )
                                                }
                                            </ul>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="modal_btn">
                        <button
                            disabled={!isFormValid || isFeeTypesLoading || (isEditMode && !isFormChanged)}
                            onClick={handleSaveDiscount}
                        >
                            {
                                isFeeTypesLoading ? (
                                    <ButtonLoader />
                                ) : (
                                    <>{isEditMode ? 'Update' : 'Save'}</>
                                )
                            }
                        </button>
                    </div>
                </div>
            </DiscountModalWrapper>
        </>
    );
}

export default DiscountModal;
